import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCcw, Check } from "lucide-react";

/**
 * Crop the source image to the given pixel region (client-side).
 * Returns a Blob (JPEG) that the caller can upload to the backend.
 * The backend re-encodes to .webp at the final display resolution.
 */
async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });

  const rad = (rotation * Math.PI) / 180;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  // Render rotated image onto a large canvas, then crop out the desired area
  const maxSide = Math.max(image.width, image.height) * 2;
  canvas.width = maxSide;
  canvas.height = maxSide;
  ctx.translate(maxSide / 2, maxSide / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const data = ctx.getImageData(0, 0, maxSide, maxSide);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(
    data,
    Math.round(-maxSide / 2 + image.width / 2 - pixelCrop.x),
    Math.round(-maxSide / 2 + image.height / 2 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
  });
}

/**
 * Modal editor: drag to pan, pinch/scroll to zoom, buttons to rotate.
 * Locked to a target aspect ratio (square for individuals, 4:3 for groups).
 */
export default function CropEditor({ file, aspect, label, onCancel, onCommit, busy }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onComplete = useCallback((_area, areaPixels) => {
    setPixelCrop(areaPixels);
  }, []);

  const handleCommit = async () => {
    if (!pixelCrop || !src) return;
    const blob = await getCroppedBlob(src, pixelCrop, rotation);
    onCommit(blob);
  };

  if (!file || !src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(30, 20, 12, 0.72)" }}
      data-testid="crop-editor"
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
    >
      <div
        className="relative w-full max-w-2xl bg-[#F9F6F0] rounded-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DFD5]">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D62839]">
              Crop &amp; adjust
            </p>
            <p className="font-display text-xl text-[#4B3A2F] leading-tight">
              {label}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            className="p-1.5 rounded-md hover:bg-[#A87149]/10"
            aria-label="Cancel"
            data-testid="crop-cancel"
          >
            <X size={18} className="text-[#4B3A2F]" />
          </button>
        </div>

        <div
          className="relative w-full bg-[#2C2724]"
          style={{ height: "min(60vh, 460px)" }}
        >
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onComplete}
            showGrid={true}
            objectFit="contain"
          />
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-[#6D5A50] w-14">
              Zoom
            </span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-grow accent-[#7E9268]"
              data-testid="crop-zoom"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-[#6D5A50] w-14">
              Rotate
            </span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-grow accent-[#7E9268]"
              data-testid="crop-rotate"
            />
            <button
              type="button"
              onClick={() => {
                setRotation(0);
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
              className="p-2 rounded-md border border-[#A87149] text-[#4B3A2F] hover:bg-[#A87149]/10"
              aria-label="Reset"
              title="Reset"
              data-testid="crop-reset"
            >
              <RotateCcw size={14} />
            </button>
          </div>
          <p className="text-[11px] text-[#6D5A50] italic text-center">
            Drag the photo to pan • pinch or scroll to zoom
          </p>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[#E7DFD5] bg-[#F3EFE6]">
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            disabled={busy}
            className="px-4 py-2 rounded-md border border-[#A87149] text-[#4B3A2F] text-sm hover:bg-[#A87149]/10 disabled:opacity-50"
            data-testid="crop-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCommit}
            disabled={busy || !pixelCrop}
            className="px-5 py-2 rounded-md bg-[#7E9268] text-white text-sm flex items-center gap-2 hover:opacity-90 disabled:opacity-60"
            data-testid="crop-save-btn"
          >
            <Check size={15} />
            {busy ? "Saving…" : "Save Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
